import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  Fade,
  Zoom,
  useTheme,
} from '@mui/material';
import {
  Casino as CasinoIcon,
  Settings as SettingsIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import SorteadorConfigModal from '../components/sorteador/SorteadorConfigModal';
import SorteadorResults from '../components/sorteador/SorteadorResults';
import SorteadorHistoricoModal from '../components/sorteador/SorteadorHistoricoModal';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface SorteadorConfig {
  id: number;
  quantidade_clientes: number;
  auto_pay_fatura: boolean;
  send_whatsapp_msg: boolean;
  aceita_inadimplente: boolean;
  qtde_dias_vencido?: number;
  tipo_cliente: 'todos' | 'pf' | 'pj';
  escritorio_ids?: number[] | null;
}

interface ClienteSorteado {
  id: number;
  nome: string;
  email?: string;
  telefone?: string;
  whatsapp?: string;
}

interface SorteioResultado {
  clientes: ClienteSorteado[];
  config: SorteadorConfig;
  totalElegiveis: number;
}

const SorteadorPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  const [config, setConfig] = useState<SorteadorConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [resultadoSorteio, setResultadoSorteio] = useState<SorteioResultado | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [sorteioMesAtual, setSorteioMesAtual] = useState<boolean>(false);

  useEffect(() => {
    carregarConfiguracoes();
    verificarSorteioMesAtual();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const response = await api.get('/sorteador/config') as ApiResponse<SorteadorConfig>;
      
      if (response.success) {
        setConfig(response.data);
      } else {
        console.error('❌ API retornou success: false:', response);
        setError('Erro ao carregar configurações do sorteador');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações:', error);
      setError('Erro ao carregar configurações do sorteador');
    }
  };

  const realizarSorteio = async () => {
    if (!config) {
      setError('Configure o sorteador antes de realizar o sorteio');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/sorteador/sortear') as ApiResponse<SorteioResultado>;
      
      if (response.success) {
        setResultadoSorteio(response.data);
        setShowResults(true);
        //setSuccess(`Sorteio realizado com sucesso! ${response.data.clientes.length} cliente(s) sorteado(s)`);
      } else {
        setError(response.message || 'Erro ao realizar sorteio');
      }
    } catch (error: any) {
      console.error('Erro ao realizar sorteio:', error);
      setError(error.response?.data?.message || 'Erro interno ao realizar sorteio');
    } finally {
      setIsLoading(false);
    }
  };

  const aplicarPremiacao = async () => {
    if (!resultadoSorteio) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/sorteador/premiar', {
        clientes: resultadoSorteio.clientes
      }) as ApiResponse<any>;

      if (response.success) {
        setSuccess('Premiações aplicadas com sucesso!');
        setShowResults(false);
        setResultadoSorteio(null);
        // Recarregar verificação do mês atual
        verificarSorteioMesAtual();
      } else {
        setError(response.message || 'Erro ao aplicar premiações');
      }
    } catch (error: any) {
      console.error('Erro ao aplicar premiações:', error);
      setError(error.response?.data?.message || 'Erro interno ao aplicar premiações');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigUpdate = async (newConfig: SorteadorConfig) => {
    try {
      const response = await api.put('/sorteador/config', newConfig) as ApiResponse<SorteadorConfig>;
      
      if (response.success) {
        setConfig(response.data);
        setSuccess('Configurações atualizadas com sucesso!');
        setShowConfigModal(false);
      } else {
        setError(response.message || 'Erro ao atualizar configurações');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar configurações:', error);
      setError(error.response?.data?.message || 'Erro interno ao atualizar configurações');
    }
  };

  const verificarSorteioMesAtual = async () => {
    try {
      const hoje = new Date();
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      const params = new URLSearchParams({
        data_inicio: primeiroDiaMes.toISOString().split('T')[0],
        data_fim: ultimoDiaMes.toISOString().split('T')[0],
      });

      const response = await api.get(`/sorteador/historico?${params}`) as ApiResponse<any[]>;
      
      if (response.success && response.data.length > 0) {
        setSorteioMesAtual(true);
      } else {
        setSorteioMesAtual(false);
      }
    } catch (error) {
      console.error('Erro ao verificar sorteio do mês:', error);
      setSorteioMesAtual(false);
    }
  };

  const limparMensagens = () => {
    setError(null);
    setSuccess(null);
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Você precisa estar logado para acessar esta página.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Cabeçalho */} 
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              <CasinoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Módulo Sorteador
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Sorteie clientes ativos para ganhar mensalidade grátis
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistoricoModal(true)}
              size="small"
              sx={{ minWidth: { md: 140 } }}
            >
              Exibir Resultados
            </Button>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={20} /> : <SettingsIcon />}
              onClick={() => setShowConfigModal(true)}
              size="small"
              sx={{ minWidth: { md: 140 } }}
            >
              Configurar Sorteador
            </Button>
          </Box>
        </Box>
      
      {/* Mensagens de feedback */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={limparMensagens}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={limparMensagens}>
          {success}
        </Alert>
      )}

      {/* Conteúdo principal - só mostra quando não há resultado de sorteio */}
      {!showResults && (
        <>
          {/* Ações principais */}
          <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <CasinoIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Realizar Sorteio
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Clique no botão abaixo para sortear {config?.quantidade_clientes || 1} cliente(s)
                  </Typography>
                  
                  {/* Alerta de sorteio já realizado no mês */}
                  {sorteioMesAtual && (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 3, textAlign: 'center', justifyContent: 'center' }}
                      icon={false}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        🎉 Sorteio do mês já realizado!
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Um sorteio já foi realizado neste mês. Clique em "Exibir Resultados" para ver os clientes sorteados.
                      </Typography>
                    </Alert>
                  )}
                  
                  <Button
                    variant="contained"
                    size="large"
                    onClick={realizarSorteio}
                    disabled={isLoading || !config}
                    startIcon={isLoading ? <CircularProgress size={20} /> : <CasinoIcon />}
                    sx={{ 
                      minWidth: 200,
                      height: 56,
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {isLoading ? 'Sorteando...' : 'Sortear Clientes'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Resultados do sorteio */}
      {showResults && resultadoSorteio && (
        <Fade in={showResults} timeout={1000}>
          <Box sx={{ mt: 4 }}>
            <SorteadorResults
              resultado={resultadoSorteio}
              onPremiar={aplicarPremiacao}
              isLoading={isLoading}
            />
          </Box>
        </Fade>
      )}

      {/* Modal de configurações */}
      <SorteadorConfigModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        config={config}
        onSave={handleConfigUpdate}
      />

      {/* Modal de histórico */}
      <SorteadorHistoricoModal
        open={showHistoricoModal}
        onClose={() => setShowHistoricoModal(false)}
      />
    </Box>
  );
};

export default SorteadorPage;
